import { Icon, PrimeRadioButton } from '@ics-portal/guardian';
import { type ReactNode, useEffect, useState } from 'react';
import { type StringOption } from '@models/Option';
import { getProjectTypePresentation } from '@utils/projectType';

interface ProjectTypeSelectorProps {
    options: StringOption[];
    value: string;
    onChange?: (value: string) => void;
    disabled?: boolean;
    showDeprecated?: boolean;
    invalid?: boolean;
}

const DEPRECATED_PREFIX = 'deprecated -';

function isDeprecated(label: string): boolean {
    return label.toLowerCase().startsWith(DEPRECATED_PREFIX);
}

export function ProjectTypeSelector({
    options,
    value,
    onChange,
    disabled,
    showDeprecated = false,
    invalid,
}: ProjectTypeSelectorProps): ReactNode {
    const [expandedValue, setExpandedValue] = useState<string | undefined>(
        value,
    );

    useEffect(() => {
        setExpandedValue(value);
    }, [value]);

    const activeOptions = options.filter((o) => !isDeprecated(o.label));
    const deprecatedOptions = options.filter((o) => isDeprecated(o.label));
    const visibleOptions = showDeprecated
        ? [...activeOptions, ...deprecatedOptions]
        : activeOptions;

    function handleOptionClick(optionValue: string): void {
        if (!disabled) {
            onChange?.(optionValue);
            return;
        }

        setExpandedValue((currentValue) => {
            if (currentValue === optionValue) {
                return undefined;
            }

            return optionValue;
        });
    }

    return (
        <div className="flex flex-column gap-2 w-full">
            {visibleOptions.map((option) => {
                const metadata = getProjectTypePresentation({
                    label: option.label,
                    typeId: option.value,
                });
                const description = metadata?.description ?? option.label;
                const isSelected = value === option.value;
                const isExpanded = disabled
                    ? expandedValue === option.value
                    : isSelected;

                return (
                    <div key={option.value} className="flex flex-column gap-2">
                        <div
                            className="flex align-items-center justify-content-between bg-n20 px-2 py-1 border-round-lg cursor-pointer"
                            style={{ minHeight: '2rem' }}
                            onClick={() => {
                                handleOptionClick(option.value);
                            }}
                        >
                            <div className="flex align-items-center gap-2">
                                <PrimeRadioButton
                                    name="projectType"
                                    value={option.value}
                                    checked={isSelected}
                                    disabled={disabled}
                                    invalid={invalid}
                                    onChange={() => {
                                        onChange?.(option.value);
                                    }}
                                />
                                {metadata ? (
                                    <Icon
                                        name={metadata.icon}
                                        fontSize="small"
                                        color={metadata.iconColor}
                                    />
                                ) : null}
                                <span className="font-bold">
                                    {option.label}
                                </span>
                            </div>
                            <i
                                className={`pi ${isExpanded ? 'pi-chevron-up' : 'pi-chevron-right'} text-sm`}
                            />
                        </div>
                        {isExpanded ? (
                            <div className="bg-n10 border-round px-3 py-2">
                                <span className="text-xs">{description}</span>
                            </div>
                        ) : null}
                    </div>
                );
            })}
        </div>
    );
}
